import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import LoginForm from './HomeMgmt/LoginForm';
import StudentManagement from './StudentMgmt';
import FeeManagement from './FeeManagement';
import Dashboard from './Dashboard';
import { AuthProvider, useAuth } from './AuthContext';
import AllFeeReport from './Report/AllFeeReport';
import ClassFeeBookMaster from './ClassFeeManagement';
import FinanceMgmt from './FinanceMgmt';
import AcademicSessionMgmt from './AcademicSessionMgmt';
import PromotionMgmt from './PromotionMgmt';
import FeeLedger from './FeeLedger';
import PaymentHistory from './PaymentHistory';
import LegacyFeeResolution from './LegacyFeeResolution';
import AttendanceManagement from './AttendanceMgmt';
import TeacherManagement from './TeacherMgmt';
import MyTeacherProfile from './TeacherMgmt/MyTeacherProfile';
import UserManagement from './UserManagement';
import ChangePassword from './UserManagement/ChangePassword';
import AuditHistory from './AuditHistory';
import TeacherWorkforce from './TeacherWorkforce';
import StudentCharges from './StudentCharges';

const feeNavigationItems = [
  { to: '/fees', label: 'Monthly Fee Charges', mark: 'FC', permission: 'FEES.VIEW' },
  { to: '/student-charges', label: 'Other Student Charges', mark: 'OC', permission: 'FEES.VIEW' },
  { to: '/class-fee-master', label: 'Fee Book Master', mark: 'FB', permission: 'FEE_BOOK.VIEW' },
  { to: '/fee-ledger', label: 'Fee Ledger', mark: 'FL', permission: 'PAYMENTS.VIEW' },
  { to: '/payment-history', label: 'Payment History', mark: 'PH', permission: 'PAYMENTS.VIEW' },
  { to: '/all-fee-report', label: 'Fee Reports', mark: 'RP', permission: 'REPORTS.VIEW' },
  { to: '/legacy-fee-review', label: 'Legacy Fee Review', mark: 'LR', permission: 'FEES.EDIT' },
];

const studentNavigationItems = [
  { to: '/students', label: 'Student Directory', mark: 'ST', permission: 'STUDENTS.VIEW' },
  { to: '/attendance', label: 'Student Attendance', mark: 'AT', permission: 'ATTENDANCE.VIEW' },
  { to: '/promotions', label: 'Student Promotion', mark: 'SP', permission: 'PROMOTIONS.VIEW' },
];

const administrationNavigationItems = [
  { to: '/teachers', label: 'Teacher Management', mark: 'TM', permission: 'TEACHERS.VIEW' },
  { to: '/teacher-workforce', label: 'Teacher Attendance & Payroll', mark: 'TP', permissions: ['TEACHER_ATTENDANCE.VIEW', 'PAYROLL.VIEW'] },
  { to: '/users', label: 'User Management', mark: 'UM', permission: 'USERS.VIEW' },
  { to: '/audit-history', label: 'Audit History', mark: 'AH', permission: 'AUDIT.VIEW' },
];

const navigationLayout = [
  { to: '/dashboard', label: 'Overview', mark: 'OV', permission: 'DASHBOARD.VIEW' },
  { key: 'students', label: 'Student Management', mark: 'SM', items: studentNavigationItems },
  { key: 'fees', label: 'Fee Management', mark: 'FM', items: feeNavigationItems },
  { to: '/academic-sessions', label: 'Academic Sessions', mark: 'AS', permission: 'SESSIONS.VIEW' },
  { key: 'administration', label: 'Administration', mark: 'AD', items: administrationNavigationItems },
  { to: '/my-teacher-profile', label: 'My Profile', mark: 'ME', permission: 'TEACHERS.VIEW_SELF', roles: ['TEACHER'] },
  { to: '/finances', label: 'Finance Register', mark: 'FR', permission: 'FINANCE.VIEW' },
];

const navigationItems = navigationLayout.flatMap((item) => item.items || [item]);

const routeItems = [
  ['/dashboard', Dashboard, 'DASHBOARD.VIEW'], ['/students', StudentManagement, 'STUDENTS.VIEW'], ['/fees', FeeManagement, 'FEES.VIEW'],
  ['/student-charges', StudentCharges, 'FEES.VIEW'],
  ['/all-fee-report', AllFeeReport, 'REPORTS.VIEW'], ['/class-fee-master', ClassFeeBookMaster, 'FEE_BOOK.VIEW'], ['/finances', FinanceMgmt, 'FINANCE.VIEW'],
  ['/academic-sessions', AcademicSessionMgmt, 'SESSIONS.VIEW'], ['/promotions', PromotionMgmt, 'PROMOTIONS.VIEW'], ['/fee-ledger', FeeLedger, 'PAYMENTS.VIEW'],
  ['/payment-history', PaymentHistory, 'PAYMENTS.VIEW'], ['/legacy-fee-review', LegacyFeeResolution, 'FEES.EDIT'], ['/attendance', AttendanceManagement, 'ATTENDANCE.VIEW'],
  ['/teachers', TeacherManagement, 'TEACHERS.VIEW'], ['/my-teacher-profile', MyTeacherProfile, 'TEACHERS.VIEW_SELF', ['TEACHER']], ['/users', UserManagement, 'USERS.VIEW'], ['/audit-history', AuditHistory, 'AUDIT.VIEW'],
  ['/teacher-workforce', TeacherWorkforce, ['TEACHER_ATTENDANCE.VIEW', 'PAYROLL.VIEW']],
];

function SidebarGroup({ group, items, onNavigate }) {
  const location = useLocation();
  const routeIsActive = items.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
  const [open, setOpen] = useState(routeIsActive);
  useEffect(() => { if (routeIsActive) setOpen(true); }, [routeIsActive]);
  const submenuId = `school-nav-${group.key}`;

  return <div className={`school-nav-group ${routeIsActive ? 'is-active' : ''} ${open ? 'is-open' : ''}`}>
    <button type="button" className="school-nav-group-toggle" aria-expanded={open} aria-controls={submenuId} onClick={() => setOpen((current) => !current)}>
      <span className="school-nav-mark">{group.mark}</span><b>{group.label}</b><i aria-hidden="true" />
    </button>
    <div id={submenuId} className="school-nav-submenu" hidden={!open}>
      {items.map((item) => <NavLink key={item.to} to={item.to} onClick={onNavigate} className={({ isActive }) => `school-nav-link school-nav-sublink ${isActive ? 'is-active' : ''}`}><span>{item.mark}</span>{item.label}</NavLink>)}
    </div>
  </div>;
}

function AppContent() {
  const { isAuthenticated, authReady, logout, can, mustChangePassword, role } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const currentRole = String(role || '').toUpperCase();
  const canOpen = (item) => (item.permissions ? item.permissions.some(can) : can(item.permission)) && (!item.roles || item.roles.includes(currentRole));
  const hasRoutePermission = (permission) => Array.isArray(permission) ? permission.some(can) : can(permission);
  const visibleNavigation = navigationItems.filter(canOpen);
  const visibleSidebarItems = navigationLayout.map((item) => item.items ? { ...item, items: item.items.filter(canOpen) } : item).filter((item) => item.items ? item.items.length : canOpen(item));
  const home = visibleNavigation[0]?.to || '/login';
  const closeSidebar = () => setSidebarOpen(false);

  if (!isAuthenticated) return <Router><main className="school-login-page"><Routes><Route path="/login" element={<LoginForm />} /><Route path="*" element={<Navigate to="/login" replace />} /></Routes></main></Router>;
  if (!authReady) return <main className="school-auth-loading">Checking account permissions…</main>;
  if (mustChangePassword) return <Router><Routes><Route path="*" element={<ChangePassword />} /></Routes></Router>;

  return <Router><div className="school-app-shell">
    <button type="button" className={`school-nav-backdrop ${sidebarOpen ? 'is-visible' : ''}`} onClick={closeSidebar} aria-label="Close navigation" />
    <aside className={`school-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
      <div className="school-brand"><span className="school-brand-mark">RS</span><div><strong>RSLM School</strong><small>Administration Portal</small></div></div>
      <p className="school-nav-caption">Permitted workspace</p>
      <nav className="school-nav" aria-label="School modules">{visibleSidebarItems.map((item) => item.items
        ? <SidebarGroup key={item.key} group={item} items={item.items} onNavigate={closeSidebar} />
        : <NavLink key={item.to} to={item.to} onClick={closeSidebar} className={({ isActive }) => `school-nav-link ${isActive ? 'is-active' : ''}`}><span>{item.mark}</span>{item.label}</NavLink>)}</nav>
      <div className="school-sidebar-footer"><p>Menus reflect your effective permissions. Every request is checked again by the server.</p><button type="button" className="school-logout" onClick={logout}>Sign out</button></div>
    </aside>
    <main className="school-main"><header className="school-topbar"><button type="button" className="school-menu-toggle" onClick={() => setSidebarOpen((current) => !current)} aria-label="Toggle navigation"><i /><i /><i /></button><div><p className="school-topbar-kicker">RSLM School</p><h1>School administration</h1></div><div className="school-topbar-status"><span />Permission-checked session</div></header>
      <section className="school-page-content"><Routes>
        <Route path="/login" element={<Navigate to={home} replace />} />
        {routeItems.map(([path, Component, permission, roles]) => <Route key={path} path={path} element={hasRoutePermission(permission) && (!roles || roles.includes(currentRole)) ? <Component /> : <Navigate to={home} replace />} />)}
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes></section>
    </main>
  </div></Router>;
}

export default function App() { return <AuthProvider><AppContent /></AuthProvider>; }
